// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Spinner, useToast } from '@loa/ui-components';
import { proposalApi } from '../api/proposalApi';
import { newBusinessApi } from '../api/newBusinessApi';
import { relationshipApi } from '../api/relationshipApi';
import { NewPensionProposalModal } from '../components/proposals/NewPensionProposalModal';
import { NewFundInvestmentProposalModal } from '../components/proposals/NewFundInvestmentProposalModal';
import { NewProtectionProposalModal } from '../components/proposals/NewProtectionProposalModal';
import {
  fundLineRows,
  proposalScalarRows,
  statusDisplay,
} from '../components/proposals/proposalDisplay';
import { ProposalPlanView } from '../components/proposals/plan/ProposalPlanView';
import { formatLastUpdated } from '../components/requests/requestCells';
import { proposalTypeLabel, type Proposal, type ProposalSection } from '../types/proposal';
import { usePageHeader } from '../contexts/PageHeaderContext';
import './ProposalDetailPage.css';

/**
 * Builder route segment for a proposal's "Continue editing" escape hatch,
 * keyed by `proposalType`. PENSION (and any future builder-active type
 * without its own entry) falls back to the sectioned pension builder.
 */
const BUILDER_ROUTE_SEGMENT: Partial<Record<Proposal['proposalType'], string>> = {
  FUND_INVESTMENT: 'investment-builder',
  PROTECTION: 'protection-builder',
  BREAKTHROUGH_REPORT: 'breakthrough-builder',
};

function builderEditRoute(proposal: Proposal): string {
  const segment = BUILDER_ROUTE_SEGMENT[proposal.proposalType] ?? 'pension-builder';
  return `/relationships/${proposal.profileId}/proposals/${segment}/${proposal.id}`;
}

/**
 * Advisor detail view for a single proposal.
 *
 * Builder-born proposals (`proposal.builder === true`) render their FULL
 * frozen plan — one read-only card per `proposal_sections` row — plus a
 * Statement of Suitability download (SENT and later; PENSION only — the
 * Breakthrough Report has its own report PDF, not yet surfaced to advisors)
 * and a "Continue editing" escape hatch back to the type's builder for
 * DRAFTs reached by URL.
 *
 * Flat-compose proposals keep the scalar "Recommendation" summary and their
 * per-type edit modals. A SENT/VIEWED proposal of either kind can be
 * withdrawn; the client's decision + comment surface here once recorded.
 */
const ProposalDetailPage: React.FC = () => {
  const { id: profileId, proposalId } = useParams<{ id: string; proposalId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { setBreadcrumbItems, setBackTo } = usePageHeader();

  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [sections, setSections] = useState<ProposalSection[]>([]);
  const [sectionsError, setSectionsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [issuingNewBusiness, setIssuingNewBusiness] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Relationship (profile) display name for the breadcrumb trail — mirrors
  // the PensionProposalBuilder's fetch exactly.
  const [relationshipName, setRelationshipName] = useState<string | null>(null);

  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;
    relationshipApi
      .getRelationship(profileId)
      .then((r) => {
        if (!cancelled) setRelationshipName(r.relationshipName || r.profileName || null);
      })
      .catch(() => {
        if (!cancelled) setRelationshipName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const load = useCallback(() => {
    if (!proposalId) return;
    setLoading(true);
    setError(null);
    setSectionsError(false);
    proposalApi
      .getProposal(proposalId)
      .then(async (p) => {
        // Builder proposals carry their plan in proposal_sections — fetch it
        // for the full read-only rendering (works for SENT+ too: the
        // endpoint returns the content frozen at share time).
        if (p.builder) {
          try {
            setSections(await proposalApi.getSections(proposalId));
          } catch {
            setSections([]);
            setSectionsError(true);
          }
        }
        setProposal(p);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load proposal'))
      .finally(() => setLoading(false));
  }, [proposalId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setBreadcrumbItems([
      { label: 'Relationships', to: '/relationships' },
      ...(profileId && relationshipName
        ? [{ label: relationshipName, to: `/relationships/${profileId}` }]
        : []),
      {
        label: 'Proposals',
        to: profileId ? `/relationships/${profileId}?tab=proposals` : undefined,
      },
    ]);
    setBackTo(profileId ? `/relationships/${profileId}?tab=proposals` : null);
    return () => {
      setBreadcrumbItems(null);
      setBackTo(null);
    };
  }, [profileId, relationshipName, setBreadcrumbItems, setBackTo]);

  const runAction = async (fn: () => Promise<Proposal>, success: string) => {
    setBusy(true);
    try {
      const updated = await fn();
      setProposal(updated);
      toast.showToast(success, 'success');
    } catch (e: unknown) {
      toast.showToast(e instanceof Error ? e.message : 'Action failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadSos = async () => {
    if (!proposal || downloading) return;
    setDownloading(true);
    try {
      const blob = await proposalApi.getSuitabilityDocument(proposal.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${proposal.proposalNumber}-statement-of-suitability.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      toast.showToast(
        e instanceof Error ? e.message : 'Failed to download the Statement of Suitability',
        'error',
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleIssueNewBusiness = async () => {
    if (!proposal || issuingNewBusiness) return;
    setIssuingNewBusiness(true);
    try {
      await newBusinessApi.createRequest(proposal.id);
      toast.showToast(
        'New Business request created — the client can now complete and sign the application.',
        'success',
      );
    } catch (e: unknown) {
      toast.showToast(
        e instanceof Error ? e.message : 'Failed to issue the New Business Application',
        'error',
      );
    } finally {
      setIssuingNewBusiness(false);
    }
  };

  if (loading) {
    return (
      <div className="proposal-detail" data-testid="proposal-detail-loading">
        <div className="proposal-detail__card proposal-detail__card--center">
          <Spinner />
        </div>
      </div>
    );
  }
  if (error || !proposal) {
    return (
      <div className="proposal-detail">
        <div className="proposal-detail__card proposal-detail__error" data-testid="proposal-detail-error">
          {error ?? 'Proposal not found'}
        </div>
      </div>
    );
  }

  const isBuilder = proposal.builder === true;
  const isDraft = proposal.status === 'DRAFT';
  const isAwaiting = proposal.status === 'SENT' || proposal.status === 'VIEWED';
  const isSentOrLater = !isDraft;
  const status = statusDisplay(proposal.status);
  const rows = isBuilder ? [] : proposalScalarRows(proposal.proposalType, proposal.details);
  const funds =
    !isBuilder && proposal.proposalType === 'FUND_INVESTMENT' ? fundLineRows(proposal.details) : [];

  const metaItems: Array<{ label: string; value: string }> = [
    ...(proposal.sentAt ? [{ label: 'Sent', value: formatLastUpdated(proposal.sentAt) }] : []),
    ...(proposal.viewedAt
      ? [{ label: 'Viewed', value: formatLastUpdated(proposal.viewedAt) }]
      : []),
    ...(proposal.decidedAt
      ? [{ label: 'Decided', value: formatLastUpdated(proposal.decidedAt) }]
      : []),
    ...(proposal.validUntil ? [{ label: 'Valid until', value: proposal.validUntil }] : []),
  ];

  return (
    <div className="proposal-detail" data-testid="proposal-detail">
      <section className="proposal-detail__card">
        <div className="proposal-detail__eyebrow">{proposal.proposalNumber}</div>
        <div className="proposal-detail__title-row">
          <h1 className="proposal-detail__title">
            {proposal.title || proposalTypeLabel(proposal.proposalType)}
          </h1>
          <div className="proposal-detail__badges">
            <Badge color="purple" size="md" data-testid="proposal-detail-type">
              {proposalTypeLabel(proposal.proposalType)}
            </Badge>
            <Badge color={status.color} size="md" data-testid="proposal-detail-status">
              {status.label}
            </Badge>
          </div>
        </div>

        {proposal.clientMessage && (
          <p className="proposal-detail__message">{proposal.clientMessage}</p>
        )}

        {metaItems.length > 0 && (
          <div className="proposal-detail__meta-row" data-testid="proposal-detail-meta">
            {metaItems.map((item) => (
              <span className="proposal-detail__meta-item" key={item.label}>
                <span className="proposal-detail__meta-label">{item.label}</span> {item.value}
              </span>
            ))}
          </div>
        )}

        {proposal.decision && (
          <div
            className={`proposal-detail__decision proposal-detail__decision--${
              proposal.decision === 'ACCEPTED' ? 'accepted' : 'rejected'
            }`}
            data-testid="proposal-detail-decision"
          >
            <strong>
              Client {proposal.decision === 'ACCEPTED' ? 'accepted' : 'rejected'} this proposal
            </strong>
            {proposal.decisionComment && (
              <p className="proposal-detail__decision-comment">“{proposal.decisionComment}”</p>
            )}
          </div>
        )}

        {proposal.decision === 'ACCEPTED' && (
          <div className="proposal-detail__header-actions">
            <Button
              variant="filled"
              data-testid="proposal-issue-new-business"
              loading={issuingNewBusiness}
              disabled={issuingNewBusiness}
              onClick={() => {
                void handleIssueNewBusiness();
              }}
            >
              Issue New Business Application
            </Button>
          </div>
        )}

        {isBuilder && (
          <div className="proposal-detail__header-actions">
            {isSentOrLater && proposal.proposalType !== 'BREAKTHROUGH_REPORT' && (
              <Button
                variant="outlined"
                data-testid="proposal-download-sos"
                disabled={downloading}
                loading={downloading}
                onClick={() => {
                  void handleDownloadSos();
                }}
              >
                Download Statement of Suitability
              </Button>
            )}
            {isDraft && (
              <Button
                variant="filled"
                data-testid="proposal-continue-editing"
                onClick={() => navigate(builderEditRoute(proposal))}
              >
                Continue editing
              </Button>
            )}
          </div>
        )}
      </section>

      {isBuilder ? (
        sectionsError ? (
          <section className="proposal-detail__card" data-testid="proposal-plan-error">
            <p className="proposal-detail__meta">Failed to load the proposal plan.</p>
          </section>
        ) : (
          <ProposalPlanView sections={sections} />
        )
      ) : (
        <section className="proposal-detail__card">
          <h2 className="proposal-detail__section-title">Recommendation</h2>
          {rows.length === 0 ? (
            <p className="proposal-detail__meta">No details captured yet.</p>
          ) : (
            <dl className="proposal-detail__grid" data-testid="proposal-detail-details">
              {rows.map((row) => (
                <div className="proposal-detail__row" key={row.label}>
                  <dt className="proposal-detail__label">{row.label}</dt>
                  <dd className="proposal-detail__value">{row.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {funds.length > 0 && (
            <table className="proposal-detail__funds" data-testid="proposal-detail-funds">
              <thead>
                <tr>
                  <th>Fund</th>
                  <th>Allocation</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {funds.map((line, i) => (
                  <tr key={`${line.fundName}-${i}`}>
                    <td>
                      {line.fundName}
                      {line.fundReference && (
                        <span className="proposal-detail__fund-ref"> · {line.fundReference}</span>
                      )}
                    </td>
                    <td>{line.allocation ?? '—'}</td>
                    <td>{line.amount ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {((isDraft && !isBuilder) || isAwaiting) && (
        <div className="proposal-detail__footer">
          {isDraft && !isBuilder && (
            <>
              <Button
                variant="filled"
                data-testid="proposal-send"
                disabled={busy}
                onClick={() => runAction(() => proposalApi.sendProposal(proposal.id), 'Proposal sent')}
              >
                Send to client
              </Button>
              <Button
                variant="outlined"
                data-testid="proposal-edit"
                disabled={busy}
                onClick={() => setEditOpen(true)}
              >
                Edit
              </Button>
            </>
          )}
          {isAwaiting && (
            <Button
              variant="outlined"
              data-testid="proposal-withdraw"
              disabled={busy}
              onClick={() => runAction(() => proposalApi.withdrawProposal(proposal.id), 'Proposal withdrawn')}
            >
              Withdraw
            </Button>
          )}
        </div>
      )}

      {!isBuilder && proposal.proposalType === 'PENSION' && (
        <NewPensionProposalModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          clients={[]}
          brokerageId={proposal.brokerageId}
          profileId={proposal.profileId}
          existingProposal={proposal}
          onUpdated={(updated) => {
            setProposal(updated);
            setEditOpen(false);
          }}
        />
      )}
      {!isBuilder && proposal.proposalType === 'FUND_INVESTMENT' && (
        <NewFundInvestmentProposalModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          clients={[]}
          brokerageId={proposal.brokerageId}
          profileId={proposal.profileId}
          existingProposal={proposal}
          onUpdated={(updated) => {
            setProposal(updated);
            setEditOpen(false);
          }}
        />
      )}
      {!isBuilder && proposal.proposalType === 'PROTECTION' && (
        <NewProtectionProposalModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          clients={[]}
          brokerageId={proposal.brokerageId}
          profileId={proposal.profileId}
          existingProposal={proposal}
          onUpdated={(updated) => {
            setProposal(updated);
            setEditOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ProposalDetailPage;
